import React, { useState, useEffect, useRef } from 'react';
import { Lock, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import styles from './form.module.css';

const TimelineOrderForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true }) => {
    const items = content?.items || [];

    const shuffled = () => {
        const arr = [...items];
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    };

    // Sanity-check a restored order against the CURRENT items before trusting it — mirrors
    // CorrectOrderForm's guard. If an admin edits the item list after a participant already
    // submitted an order for the old list (or mid-session while they have it open), a
    // length-mismatched restored order would otherwise leave the UI referencing stale/missing
    // items instead of falling back to a fresh shuffle of the current items.
    const initOrder = () => {
        if (userAnswer?.order && Array.isArray(userAnswer.order) && userAnswer.order.length === items.length) {
            return [...userAnswer.order];
        }
        return items.length ? shuffled() : [];
    };

    const [order, setOrder] = useState(initOrder);
    const dragIdx = useRef(null);
    const [dragOverIdx, setDragOverIdx] = useState(null);
    // Guards against a socket-reconnect resync (JoinPage re-fetches the active activity and
    // calls setUserAnswer with a freshly-parsed object on every reconnect, even when nothing
    // actually changed) silently clobbering a reorder the participant is mid-way through but
    // hasn't submitted yet. Flips true the moment a drag/move happens since the last submit,
    // and resets on a successful submit or when userAnswer goes back to null (a genuinely new
    // activity, where there's nothing local worth preserving).
    const hasLocalEditsRef = useRef(false);

    useEffect(() => {
        if (userAnswer == null) {
            hasLocalEditsRef.current = false;
        }
        if (hasLocalEditsRef.current) return;
        setOrder(initOrder());
    }, [userAnswer, items]);

    const hasSubmitted = userAnswer != null;
    const isDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);
    const isUnchanged = hasSubmitted && JSON.stringify(order) === JSON.stringify(userAnswer?.order || []);
    const submitDisabled = isPreview || isLocked || order.length === 0 || (hasSubmitted && (!allowUpdateResponse || isUnchanged));
    const submitLabel = !hasSubmitted ? (isLocked ? 'Voting Closed' : 'Submit') : (!allowUpdateResponse || isUnchanged ? 'Submitted ✓' : 'Update response');

    const handleDragStart = (i) => { if (!isDisabled) dragIdx.current = i; };
    const handleDragOver = (e, i) => { e.preventDefault(); setDragOverIdx(i); };
    const handleDrop = (i) => {
        if (dragIdx.current === null || dragIdx.current === i) return;
        hasLocalEditsRef.current = true;
        setOrder(prev => {
            const next = [...prev];
            const [moved] = next.splice(dragIdx.current, 1);
            next.splice(i, 0, moved);
            return next;
        });
        dragIdx.current = null;
        setDragOverIdx(null);
    };

    const handleSubmit = () => {
        if (submitDisabled) return;
        onSubmit({ order });
        hasLocalEditsRef.current = false;
    };

    // Touch/keyboard fallback: native HTML5 drag-and-drop never fires on mobile
    // touch devices, so moving an item up/down via tap or Enter/Space swaps it
    // with its neighbor in the same `order` array the drag handlers mutate.
    const moveItem = (index, direction) => {
        if (isDisabled) return;
        const target = index + direction;
        if (target < 0 || target >= order.length) return;
        hasLocalEditsRef.current = true;
        setOrder(prev => {
            const next = [...prev];
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    };

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content.question || 'Arrange in chronological order'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden="true" /> Voting Locked</span>}
            </div>
            {!isDisabled && (
                <span className={styles.rankingInstructions}>
                    Drag and drop items to arrange them in the correct chronological order.
                </span>
            )}
            <div className={styles.correctOrderList}>
                {order.map((item, i) => (
                    <div
                        key={item}
                        className={`${styles.correctOrderItem} ${dragOverIdx === i ? styles.correctOrderItemDragOver : ''}`}
                        draggable={!isDisabled}
                        onDragStart={() => handleDragStart(i)}
                        onDragOver={(e) => handleDragOver(e, i)}
                        onDrop={() => handleDrop(i)}
                        onDragEnd={() => setDragOverIdx(null)}
                        aria-disabled={isDisabled}
                        aria-label={`${item}, position ${i + 1} of ${order.length}${isDisabled ? '' : ', drag to reorder'}`}
                    >
                        <span className={styles.correctOrderGrip} aria-hidden="true"><GripVertical size={18} /></span>
                        <span className={styles.correctOrderMoveGroup}>
                            <button
                                type="button"
                                className={styles.stepperBtn}
                                onClick={() => moveItem(i, -1)}
                                disabled={isDisabled || i === 0}
                                aria-label="Move up"
                            >
                                <ArrowUp size={16} />
                            </button>
                            <button
                                type="button"
                                className={styles.stepperBtn}
                                onClick={() => moveItem(i, 1)}
                                disabled={isDisabled || i === order.length - 1}
                                aria-label="Move down"
                            >
                                <ArrowDown size={16} />
                            </button>
                        </span>
                        <span className={styles.correctOrderNumber}>{i + 1}</span>
                        <span className={styles.correctOrderLabel}>{item}</span>
                    </div>
                ))}
            </div>
            <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitDisabled}>
                {submitLabel}
            </button>
        </div>
    );
};

export default TimelineOrderForm;
